import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { UserService } from 'src/app/modules/account/shared/user.service';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { MessageService } from 'src/app/modules/core/services/message.service';
import { LocalStoreService } from '../../services/local-store.service';
import { WelcomeModalService } from '../../services/welcome-modal.service';
import { RegisterPopupComponent } from '../register-popup/register-popup.component';
import {
  ensureRegistrationDeviceId,
  isRegistrationLimitedError,
  registrationRetryAtFromError,
  REGISTRATION_RETRY_AT_KEY,
} from '../../services/registration-limit.util';
import { environment } from 'src/environments/environment';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
@Component({
  selector: 'app-login-popup',
  templateUrl: './login-popup.component.html',
  styleUrls: ['./login-popup.component.scss'],
})
export class LoginPopupComponent implements OnInit, OnDestroy {
  private readonly isBrowser: boolean;
  private destroy$ = new Subject<void>();
  private registrationRetryTimerSub: Subscription | null = null;
  loginForm: FormGroup;
  loading = false;
  errors: any = [];
  hidePass = true;
  reload: boolean = true;
  private returnUrl: any;
  reChaptra = false;
  isLoggedIn = false;
  /** One-click registration in flight — shows the full-card overlay. */
  registering = false;
  countries: any[] = [];
  selectedCountryId: number | null = null;
  selectedCountry: any;
  private deviceId: string | null = null;
  registrationLimited = false;
  registrationRetryAt: string | null = null;
  registrationLimitMessage = '';
  registrationLimitHelper = '';
  countryFlags = {
    MAD: 'https://firebasestorage.googleapis.com/v0/b/tabbassi-test.appspot.com/o/countries%2Fmorocco.png?alt=media&token=e75a5c1b-432d-477a-b4ea-1296e4dfbd81',
    TND: 'https://firebasestorage.googleapis.com/v0/b/tabbassi-test.appspot.com/o/countries%2Ftunusia.png?alt=media&token=73653d96-289a-4549-b380-5d615094a86f',
  };

  googleRecaptchaSiteKey = environment.GOOGLE_RECAPTCHA_SITE_KEY;
  constructor(
    private router: Router,
    private authService: AuthService,
    private fb: FormBuilder,
    public ref: DynamicDialogRef,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private userService: UserService,
    private translateService: TranslateService,
    private localStoreService: LocalStoreService,
    private welcomeService: WelcomeModalService,
    public config: DynamicDialogConfig,
    public dialogService: DialogService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser && window.innerWidth <= 768) {
      // Match the global `.login-modal` mobile min-width (92vw) — a smaller
      // value exposes a strip of the theme-white dialog background.
      this.config.width = '92vw';
      this.config.contentStyle = {
        ...this.config.contentStyle,
        'max-width': '92vw',
      };
    }
  }

  /**
   * On init
   */
  ngOnInit(): void {
    // this.localStoreService.removeItem('user');
    // this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    if (
      this.config?.data?.reload != null ||
      this.config?.data?.reload != undefined
    ) {
      this.reload = this.config?.data?.reload;
    }
    this.initLoginForm();
    this.loadCountries();
    this.deviceId = ensureRegistrationDeviceId(
      this.localStoreService,
      this.isBrowser
    );
    this.restoreRegistrationLimitFromStorage();
    // While this popup is open the post-registration welcome stays hidden —
    // it must not cover the one-click credentials screen.
    this.welcomeService.suppress();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.loading = false;
    this.stopRegistrationRetryTimer();
    this.destroy$.next();
    this.destroy$.complete();
    this.welcomeService.release();
  }

  /** True while the backend 429 window is active — disables the register flow. */
  get registrationBlocked(): boolean {
    return this.registrationLimited;
  }

  initLoginForm(): void {
    this.loginForm = this.fb.group({
      username: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
        ]),
      ],
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
        ]),
      ],
    });
  }

  async submit(): Promise<any> {
    if (this.loginForm.valid) {
      this.loading = true;
      const resData = {
        username: this.loginForm.value.username,
      };
      this.userService
        .getUserEmailByUsername(resData)
        .subscribe(async (res) => {
          if (res.success) {
            const result = await this.authService.loginWithMail({
              email: res.data,
              password: this.loginForm.value.password,
            });
            this.checkResult(result);
            this.loading = false;
            this.ref.close(true);
          } else {
            this.loading = false;
            this.messageService.showPopup(
              this.translateService.instant('ALERT.USERNAME_NOTFOUND'),
              'alert',
              'fail',
              'p0Popup'
            );
          }
        });
    } else {
      const controls = this.loginForm.controls;
      Object.keys(controls).forEach((controlName) =>
        controls[`${controlName}`].markAsTouched()
      );
      return;
    }
  }

  handleReChaptra() {
    if (this.registrationBlocked) {
      return;
    }
    this.reChaptra = true;
  }

  loadCountries(): void {
    this.authService
      .getCountriesPartial(1, 1000)
      .then((response) => {
        let countriesList = [];

        if (response && Array.isArray(response)) {
          countriesList = response;
        } else if (response && Array.isArray(response.data)) {
          countriesList = response.data;
        }

        if (countriesList.length > 0) {
          // Her ülkeye flag ekle (eğer yoksa currency'ye göre)
          let mappedCountries = countriesList.map((country: any) => ({
            ...country,
            flag:
              country.countryFlag ||
              this.countryFlags[country.currency] ||
              null,
          }));
          
          // Seçili ülkeyi (id=1) listenin en başına al
          const countryWithId1 = mappedCountries.find(
            (country) => country.id === 1
          );
          if (countryWithId1) {
            // id=1 olan ülkeyi çıkar ve başa ekle
            mappedCountries = [
              countryWithId1,
              ...mappedCountries.filter((c) => c.id !== 1)
            ];
            this.selectedCountryId = 1;
            this.selectedCountry = countryWithId1;
          } else if (mappedCountries.length > 0) {
            // Eğer id 1 bulunamazsa ilk ülkeyi seç
            this.selectedCountryId = mappedCountries[0].id;
            this.selectedCountry = mappedCountries[0];
          }
          
          this.countries = mappedCountries;
        }
      })
      .catch((err) => {
        console.error('Ülke listesi yüklenirken hata oluştu:', err);
      });
  }

  onCountryChange(event: any) {
    const country = this.countries.find(
      (country) => country.id === event.value
    );
    if (country) {
      this.selectedCountryId = country.id;
      this.selectedCountry = country;
    }
  }

  resolved(captchaResponse: string) {
    if (this.registrationBlocked || this.registering) {
      return;
    }
    this.registering = true;
    this.reChaptra = false;

    // Default olarak id: 1 gönderilmeli (eğer seçili değer yoksa)
    const countryIdToSend =
      this.selectedCountryId && this.selectedCountryId > 0
        ? this.selectedCountryId
        : 1;

    this.authService
      .oneClickRegistration(captchaResponse, countryIdToSend, this.deviceId)
      .then((response) => {
        this.clearRegistrationLimit();
        const credentials = {
          username: response?.username,
          password: response?.password,
        };
        // Queue the one-time welcome modal in CREDENTIALS mode — it replaced
        // the old in-popup credentials screen and shows the moment this
        // popup closes (right after the auto-login below).
        this.welcomeService.markPending(
          credentials.username && credentials.password ? credentials : null
        );
        this.loginForm.patchValue(credentials);

        this.loginWithUserCredentials(credentials);
      })
      .catch((err) => {
        this.registering = false;
        if (this.isRegistrationLimitedError(err)) {
          this.applyRegistrationLimit(err);
          return;
        }
        this.messageService.showPopup(
          this.translateService.instant('ALERT.SOMETHING_WENT_WRONG'),
          'alert',
          'fail',
          'p0Popup'
        );
      });
  }

  // Opens the standard-registration modal ON TOP of this popup. Closing it
  // reveals the login form again; a successful registration auto-logged the
  // user in, so this popup closes too (and reloads when the opener asked).
  openRegisterPopup(): void {
    if (this.registrationBlocked) {
      return;
    }
    const regRef = this.dialogService.open(RegisterPopupComponent, {
      showHeader: true,
      dismissableMask: true,
      closable: true,
      // login-modal supplies the proven dialog chrome (flush header with
      // overlaid close icon, mobile sizing); register-modal is the
      // register-specific override hook.
      styleClass: 'login-modal register-modal',
      data: { reload: this.reload },
      // Explicit width at EVERY breakpoint: with an auto-width dialog the
      // form's intrinsic width changes as validation messages appear
      // (e.g. while typing a short password), visibly resizing the modal.
      width:
        typeof window !== 'undefined' && window.innerWidth > 768
          ? '560px'
          : '92vw',
      contentStyle: {
        'border-radius': '4px',
        padding: '0',
        'max-height': '90vh',
        overflow: 'auto',
      },
      baseZIndex: 10000,
    });
    regRef.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      // A 429 inside the register modal persisted the shared retryAt —
      // re-read it so this popup reflects the block too.
      this.restoreRegistrationLimitFromStorage();
      if (result === 'registered') {
        this.isLoggedIn = true;
        if (this.isBrowser && this.reload) {
          // A reload is imminent: hold the welcome modal so the pending flag
          // is NOT consumed (and flashed) in the teardown race — the fresh
          // page boot shows it instead. The unbalanced suppress dies with
          // the page.
          this.welcomeService.suppress();
        }
        this.ref.close(true);
        if (this.isBrowser && this.reload) {
          window.location.reload();
        }
      }
      // 'registered-login-failed': account exists but auto-login failed —
      // stay on the login form so the user can sign in manually.
    });
  }

  private isRegistrationLimitedError(err: any): boolean {
    return isRegistrationLimitedError(err);
  }

  private applyRegistrationLimit(err: any): void {
    const retryAt = registrationRetryAtFromError(err);

    this.registrationLimited = true;
    this.registrationRetryAt = retryAt;
    this.reChaptra = false;
    this.updateRegistrationLimitMessages();
    this.persistRegistrationLimit(retryAt);
    this.startRegistrationRetryTimer();
  }

  private updateRegistrationLimitMessages(): void {
    if (!this.registrationRetryAt) {
      this.registrationLimitMessage = '';
      this.registrationLimitHelper = '';
      return;
    }
    const retryDate = new Date(this.registrationRetryAt);
    const msLeft = retryDate.getTime() - Date.now();
    const minutes = Math.max(1, Math.ceil(msLeft / 60000));
    const hh = String(retryDate.getHours()).padStart(2, '0');
    const mm = String(retryDate.getMinutes()).padStart(2, '0');
    this.registrationLimitMessage = this.translateService.instant(
      'AUTH.REGISTER.TEMPORARILY_LIMITED',
      { time: `${hh}:${mm}`, minutes }
    );
    this.registrationLimitHelper = this.translateService.instant(
      'AUTH.REGISTER.TEMPORARILY_LIMITED_HELPER'
    );
  }

  private persistRegistrationLimit(retryAt: string): void {
    this.localStoreService.setItem(REGISTRATION_RETRY_AT_KEY, retryAt);
  }

  private restoreRegistrationLimitFromStorage(): void {
    const retryAt = this.localStoreService.getItem<string>(
      REGISTRATION_RETRY_AT_KEY
    );
    if (!retryAt) {
      return;
    }
    if (new Date(retryAt).getTime() <= Date.now()) {
      this.clearRegistrationLimit();
      return;
    }
    this.registrationLimited = true;
    this.registrationRetryAt = retryAt;
    this.updateRegistrationLimitMessages();
    this.startRegistrationRetryTimer();
  }

  private clearRegistrationLimit(): void {
    this.registrationLimited = false;
    this.registrationRetryAt = null;
    this.registrationLimitMessage = '';
    this.registrationLimitHelper = '';
    this.localStoreService.removeItem(REGISTRATION_RETRY_AT_KEY);
    this.stopRegistrationRetryTimer();
  }

  private startRegistrationRetryTimer(): void {
    if (!this.isBrowser) {
      return;
    }
    this.stopRegistrationRetryTimer();
    this.registrationRetryTimerSub = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          !this.registrationRetryAt ||
          new Date(this.registrationRetryAt).getTime() <= Date.now()
        ) {
          this.clearRegistrationLimit();
          return;
        }
        this.updateRegistrationLimitMessages();
      });
  }

  private stopRegistrationRetryTimer(): void {
    if (this.registrationRetryTimerSub) {
      this.registrationRetryTimerSub.unsubscribe();
      this.registrationRetryTimerSub = null;
    }
  }

  private loginWithUserCredentials(user: any): void {
    this.loading = true;
    const resData = {
      username: user.username,
    };
    this.userService.getUserEmailByUsername(resData).subscribe(
      async (res) => {
        if (res.success) {
          const result = await this.authService.loginWithMail({
            email: res.data,
            password: user.password,
          });
          this.checkResultWithoutClosing(result);
          this.loading = false;
          if (result && result.success) {
            // Hand over to the welcome modal (credentials mode) — it opens
            // as soon as this popup's destroy releases the suppression hold.
            // `registering` stays true so the overlay carries seamlessly
            // into the close animation.
            this.ref.close(true);
          } else {
            this.registering = false;
          }
        } else {
          this.loading = false;
          this.registering = false;
          this.messageService.showPopup(
            this.translateService.instant('ALERT.USERNAME_NOTFOUND'),
            'alert',
            'fail',
            'p0Popup'
          );
        }
      },
      () => {
        this.loading = false;
        this.registering = false;
        this.messageService.showPopup(
          this.translateService.instant('ALERT.SOMETHING_WENT_WRONG'),
          'alert',
          'fail',
          'p0Popup'
        );
      }
    );
  }

  private checkResultWithoutClosing(result: any): any {
    if (!result.success) {
      this.messageService.showPopup(
        this.translateService.instant(result.message),
        'alert',
        result.success ? 'success' : 'fail',
        'p0Popup'
      );
    } else if (result.success) {
      this.isLoggedIn = true;
      this.authService.refreshUserAccount();
    }
  }

  checkResult(result: any): any {
    if (!result.success) {
      this.messageService.showPopup(
        this.translateService.instant(result.message),
        'alert',
        result.success ? 'success' : 'fail',
        'p0Popup'
      );
    } else if (result.success && this.isBrowser) {
      this.reload && window.location.reload();
    }
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.loginForm.controls[`${controlName}`];
    if (!control) {
      return false;
    }

    const result =
      control.hasError(`${validationType}`) &&
      (control.dirty || control.touched);
    return result;
  }
}
